import i18n from '@/i18n';
import { NUMBERED_ERRORS } from '@/constants/Errors';
import { ApiErrorInterface } from '@/models/ApiClientError';

const TOAST_TIMEOUT_BY_TEXT_LENGTH = [
  { length: 200, timeout: 20000 },
  { length: 100, timeout: 10000 },
  { length: 0, timeout: 4000 },
];

export const getTimeoutForToast = (text: string): number | undefined => {
  for (const toastOption of TOAST_TIMEOUT_BY_TEXT_LENGTH) {
    if (text.length > toastOption.length) {
      return toastOption.timeout;
    }
  }

  return undefined;
};

const getDefaultApiError = ({
  i18nGroupKey,
  defaultErrorI18nKey,
}: {
  i18nGroupKey?: string;
  defaultErrorI18nKey?: string;
} = {}) => {
  const defaultError = defaultErrorI18nKey ?? 'unknownError';
  if (i18n.global.te(defaultError)) {
    return i18n.global.t(defaultError);
  }

  return i18n.global.t(`${i18nGroupKey}.${defaultError}`);
};

export const deprecatedGetApiError = (
  error: ApiErrorInterface,
  i18nGroupKey?: string,
  defaultErrorI18nKey?: string,
  payload?: Record<string, unknown>,
) => {
  const groupKey = i18nGroupKey ? `${i18nGroupKey}.` : '';
  const errorName = `${groupKey}errors.${error?.data?.name}`;
  if (i18n.global.te(errorName)) {
    return payload ? i18n.global.t(errorName, payload) : i18n.global.t(errorName);
  }

  return getDefaultApiError({ i18nGroupKey, defaultErrorI18nKey });
};

export const getApiError = (
  error: ApiErrorInterface,
  {
    translationParams,
    defaultErrorI18nKey,
  }: { translationParams?: Record<string, unknown>; i18nGroupKey?: string; defaultErrorI18nKey?: string } = {},
) => {
  const errorKey = `apiErrors.${error?.data?.name}`;
  if (i18n.global.te(errorKey)) {
    return translationParams ? i18n.global.t(errorKey, translationParams) : i18n.global.t(errorKey);
  }

  return getDefaultApiError({ defaultErrorI18nKey });
};

export const getNumberedApiError = (
  error: ApiErrorInterface,
  i18nGroupKey: string,
  defaultErrorI18nKey?: string,
  payload?: { [key: string]: string | number },
) => {
  const translatedError = getApiError(error);
  const deprecatedTranslatedError = deprecatedGetApiError(error, i18nGroupKey, defaultErrorI18nKey, payload);
  const defaultError = getDefaultApiError();

  const errorName = error?.data?.name;
  if (!errorName) {
    // fallback to deprecated translation method when translation hasn't been migrated yet
    if (translatedError === defaultError) return deprecatedTranslatedError;

    return translatedError;
  }
  const number = NUMBERED_ERRORS[errorName];
  if (!number) {
    return translatedError;
  }
  const errorNumber = i18n.global.t('numberedErrors.errorNumber', { number });

  // fallback to deprecated translation method when translation hasn't been migrated yet
  if (translatedError === defaultError) return `${errorNumber} ${deprecatedTranslatedError}`;

  return `${errorNumber} ${translatedError}`;
};
